<script setup lang="ts">
  import { IconSettings, IconFileDownload } from '@tabler/icons-vue';
  import { useControllersStore } from '@/stores/controllers';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { showToast } from '@/utils/displayError';
  import { useGroupsFilter } from '@/views/VControllersView/useGroupsFilter';
  import { useSearchFilter } from '@/views/VControllersView/useSearchFilter';
  import type { VHeaderProps } from './types';

  withDefaults(defineProps<VHeaderProps>(), {
    breadcrumbs: () => [],
  });

  const route = useRoute();
  const pageAnchor = computed(() =>
    route?.name ? `page.${String(route.name)}` : '',
  );
  const badgeAnchor = computed(
    () => (route?.meta?.badgeAnchor as string | undefined) ?? pageAnchor.value,
  );
  const organizationsStore = useOrganizationsStore();
  const controllersStore = useControllersStore();
  const { search, searchBy } = useSearchFilter();
  const { groupIds } = useGroupsFilter();

  const menu = ref();
  const toggle = (event: Event) => {
    menu.value.toggle(event);
  };

  const { t } = useI18n({
    messages: {
      ru: {
        orgSettings: 'Настройки организации',
        exportData: 'Выгрузить данные',
        exportCSV: 'Выгрузить в csv',
        exportXSLX: 'Выгрузить в xslx',
        exportLimit:
          'Выгружены первые {limit} контроллеров из {count}. Чтобы выгрузить остальные, сузьте фильтр или выберите группу',
      },
      en: {
        orgSettings: 'Organization settings',
        exportData: 'Export data',
        exportCSV: 'Export to csv',
        exportXSLX: 'Export to xslx',
        exportLimit:
          'Exported the first {limit} of {count} controllers. To export the rest, narrow the filter or pick a group',
      },
    },
  });

  const getExportRows = async () => {
    const { controllers, count, isCut } =
      await controllersStore.getAllControllers({
        serialNumber: searchBy.serialNumber ? search.value : '',
        groupName: searchBy.groupName ? search.value : '',
        firmwareVersion: searchBy.firmwareVersion ? search.value : '',
        description: searchBy.description ? search.value : '',
        groupIds: groupIds.value,
        organizationId: organizationsStore.organization?.id as string,
      });

    if (isCut) {
      showToast(
        t('exportLimit', { limit: controllers.length, count }),
        'warn',
      );
    }

    return controllers.map(
      ({
        serialNumber,
        isAgentOk,
        lastAgentPingAt,
        firmwareVersion,
        description,
        group,
      }) => [
        serialNumber,
        isAgentOk ? 'online' : 'problem',
        firmwareVersion,
        description,
        group?.name,
        lastAgentPingAt ? new Date(lastAgentPingAt) : '',
      ],
    );
  };

  const downloadXSLX = async () => {
    const rows = await getExportRows();
    const { exportToXLSX } = await import('@/utils/exportTable');

    exportToXLSX(rows, 'controllers', [
      { wch: 12 },
      { wch: 8 },
      { wch: 15 },
      { wch: 40 },
      { wch: 25 },
      { wch: 16 },
    ]);
  };

  const downloadCSV = async () => {
    const rows = await getExportRows();
    const { exportToCSV } = await import('@/utils/exportTable');

    exportToCSV(rows, 'controllers');
  };

  const items = computed(() => [
    {
      label: t('exportCSV'),
      command: downloadCSV,
    },
    {
      label: t('exportXSLX'),
      command: downloadXSLX,
    },
  ]);
</script>

<template>
  <header
    class="flex flex-wrap justify-between items-center content-end gap-16 mt-16 overflow-hidden max-w-full min-w-[0px]">
    <VLogo />
    <div class="flex flex-grow flex-wrap gap-16 max-w-full">
      <div class="flex-grow flex flex-col justify-center min-w-[0px]">
        <div v-if="title" class="flex items-center gap-8 min-w-[0px]">
          <h2 class="text-xl truncate">{{ title }}</h2>
          <VFeatureBadge v-if="badgeAnchor" :anchor="badgeAnchor" />
          <VDocLink v-if="pageAnchor" :place="pageAnchor" />
        </div>
        <VBreadcrumbs v-if="breadcrumbs.length" :items="breadcrumbs">
          <template #separator>/</template>
        </VBreadcrumbs>
      </div>

      <div class="flex gap-16 h-48 max-w-full">
        <template v-if="route?.name === 'controllers'">
          <Button
            v-tooltip.bottom="menu ? null : t('exportData')"
            type="button"
            data-testid="downloadCSV"
            :disabled="!controllersStore.controllers?.length"
            severity="secondary"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            @click="toggle">
            <IconFileDownload />
            <VFeatureBadge anchor="controllers.export" />
          </Button>
          <Menu id="overlay_menu" ref="menu" :model="items" :popup="true" />

          <Button
            v-if="organizationsStore.can('organization.manage')"
            v-tooltip.bottom="t('orgSettings')"
            severity="secondary"
            data-testid="orgSettings">
            <RouterLink
              :to="{
                name: 'organization',
                params: {
                  organizationId: organizationsStore.organization?.id,
                },
              }">
              <IconSettings />
            </RouterLink>
            <VFeatureBadge anchor="organization.settings" />
          </Button>
        </template>

        <VUser />
      </div>
    </div>
  </header>
</template>
